import { create } from "zustand";

import { fetchWithAuth } from "../api/fetchWithAuth";

export const useDatasetsStore = create((set, get) => ({

  datasets: [],
  loading: false,
  error: null,
  warming: {},

  loadDatasets: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetchWithAuth("/api/v1/platform/datasets", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("kc_token")}`,
        },
      });
      if (!res.ok) throw new Error(`Failed to load datasets: ${res.status}`);
      const data = await res.json();
      set({ datasets: data.datasets ?? [], loading: false });
    } catch (e) {
      set({ error: e.message, loading: false });
    }
  },

  // ── WARM A DATASET'S CACHE PARTITION ───────────────────────────────────────
  //
  // THIS USED TO POST TO A ROUTE THAT DOES NOT EXIST.
  // `/api/v1/platform/datasets/:key/warm` returns 404 -- nothing in admin-api
  // registers it -- so the button had never reached a warmer, and because the
  // only failure handling was a `finally` that cleared the spinner, it looked
  // like a warm that finished instantly. The real endpoint is
  // POST /api/v1/platform/warm-jobs, which 202s with the job row and runs
  // fire-and-forget, so progress has to be polled.
  //
  // `warming[key]` is now an OBJECT, not a boolean, because "warming" was never
  // the interesting state. A job reports attempted/total, and a dataset that is
  // wholly superseded by a newer collect correctly warms ZERO tiles -- that is a
  // real answer, not a no-op, and the caller has to be able to tell it apart
  // from a failure.
  //
  // No Authorization header is set here: fetchWithAuth injects a freshly
  // refreshed token and applies it AFTER spreading options.headers, so a token
  // read from localStorage would be redundant at best and stale at worst -- and
  // a warm can outlive the token that started it.
  warmDataset: async (datasetKey) => {
    const put = (v) =>
      set((state) => ({ warming: { ...state.warming, [datasetKey]: v } }));

    put({ phase: "starting", attempted: 0, total: null });

    let job;
    try {
      const res = await fetchWithAuth("/api/v1/platform/warm-jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dataset_key: datasetKey, concurrency: 8 }),
      });
      if (!res.ok) throw new Error(`warm-jobs ${res.status}`);
      job = await res.json();
    } catch (e) {
      // The old version threw inside a try whose only handler was `finally`,
      // so a failure surfaced as an unhandled rejection and the button simply
      // stopped saying "Warming…". Failures are now a visible terminal state.
      put({ phase: "error", message: String(e.message || e) });
      setTimeout(() => put(undefined), 8000);
      return;
    }

    // Poll until terminal. 1s against a 750ms server-side progress flush, so a
    // stale figure is never read twice running. The cap is a backstop for a
    // runner that dies without writing a terminal status -- it stops the pill
    // spinning forever, and says so rather than implying success.
    const started = Date.now();
    const MAX_MS = 15 * 60 * 1000;

    for (;;) {
      await new Promise((r) => setTimeout(r, 1000));
      let row;
      try {
        const r = await fetchWithAuth(`/api/v1/platform/warm-jobs/${job.id}`);
        if (!r.ok) throw new Error(`status ${r.status}`);
        row = await r.json();
      } catch {
        if (Date.now() - started > MAX_MS) {
          put({ phase: "error", message: "lost contact with job" });
          setTimeout(() => put(undefined), 8000);
          return;
        }
        continue; // a transient poll failure is not a failed warm
      }

      const terminal = ["succeeded", "failed", "cancelled"].includes(row.status);
      if (!terminal) {
        // total_tiles is 0 until the worklist is enumerated, and enumeration is
        // a ~3.5s geometry query (the latest-with-coverage difference across
        // every granule). Rendering "0/0" during that window reads as a
        // finished no-op -- which is also a REAL terminal result for a
        // superseded dataset, so the two must not look alike. Until a total
        // exists the job is still starting, and says so.
        const total = row.total_tiles ?? 0;
        put(total > 0
          ? { phase: "running", attempted: row.attempted_tiles ?? 0, total }
          : { phase: "starting", attempted: 0, total: null });
        if (Date.now() - started > MAX_MS) {
          put({ phase: "error", message: "timed out waiting for job" });
          setTimeout(() => put(undefined), 8000);
          return;
        }
        continue;
      }

      // Refresh the row so the coverage pill reflects the job that just ran.
      // Without this the pill keeps showing the PREVIOUS job's numbers until
      // something else reloads the page -- which for a warm that just moved
      // 0 -> 262 is the one moment it is most obviously wrong.
      get().loadDatasets();

      put({
        phase: row.status === "succeeded" ? "done" : "error",
        attempted: row.attempted_tiles ?? 0,
        total: row.total_tiles ?? 0,
        stored: row.warmed_tiles ?? 0,
        alreadyWarm: row.already_warm_tiles ?? 0,
        incomplete: row.incomplete_tiles ?? 0,
        message: row.last_error || null,
      });
      setTimeout(() => put(undefined), 12000);
      return;
    }
  },

  setDatasets: (datasets) =>
    set({
      datasets,
      loading: false,
      error: null
    }),

  addDataset: (dataset) =>
    set((state) => ({
      datasets: [...state.datasets, dataset]
    })),

  updateDataset: (datasetId, updates) =>
    set((state) => ({
      datasets: state.datasets.map((d) =>
        d.id === datasetId ? { ...d, ...updates } : d
      )
    })),

  removeDataset: (datasetId) =>
    set((state) => ({
      datasets: state.datasets.filter((d) => d.id !== datasetId)
    })),

  setLoading: (loading) =>
    set({ loading }),

  setError: (error) =>
    set({ error }),

  reset: () =>
    set({
      datasets: [],
      loading: false,
      error: null
    })

}));